import { useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader2, Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatDate, formatMoney, formatMonth, formatNumber } from "@/lib/format";
import { CompanyInitials } from "@/components/companies/CompanyInitials";
import { NewAwardsChip } from "@/components/companies/CompaniesSidebar";
import type { CompanyItem } from "@/types/domain";

// Empresa · Consorcios · Contratos · Última adj. · Monto y recursos · Acciones.
export const COMPANY_GRID_COLS =
  "minmax(240px,1.6fr) minmax(150px,1fr) 80px 100px minmax(200px,1fr) 170px";

/**
 * Una empresa del listado. Los ítems que calzan con los recursos buscados se
 * despliegan debajo: son la prueba de que compra lo que el usuario vende, y se
 * ven aunque la empresa esté bloqueada, porque son lo que engancha a desbloquearla.
 */
export function CompanyResultRow({
  company,
  following,
  newAwards,
  defaultExpanded,
  unlocking,
  onToggleFollow,
  onUnlock,
  onOpen,
}: {
  company: CompanyItem;
  following: boolean;
  newAwards: number;
  defaultExpanded: boolean;
  unlocking: boolean;
  onToggleFollow: () => void;
  onUnlock: () => void;
  onOpen: () => void;
}) {
  const matches = company.matchedPriceItems ?? [];
  const [expanded, setExpanded] = useState(defaultExpanded && matches.length > 0);
  const matchesAmount = matches.reduce((sum, match) => sum + match.amount, 0);
  const locked = company.isLocked;

  return (
    <div>
      <div
        className="grid items-center gap-4 px-6 py-4"
        style={{ gridTemplateColumns: COMPANY_GRID_COLS }}
      >
        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 cursor-pointer items-center gap-3 text-left"
        >
          {locked ? (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400 dark:bg-slate-800">
              <Lock className="size-4" />
            </div>
          ) : (
            <CompanyInitials name={company.company_name ?? ""} />
          )}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              {locked ? (
                // Data fantasma: el nombre es el lead, se ve recién al desbloquear.
                <span
                  aria-hidden
                  className="select-none truncate text-sm font-extrabold uppercase text-slate-400 blur-[5px]"
                >
                  Constructora Ejecutora SAC
                </span>
              ) : (
                <span
                  className="truncate text-sm font-extrabold uppercase text-slate-800 hover:underline dark:text-slate-100"
                  title={company.company_name ?? undefined}
                >
                  {company.company_name}
                </span>
              )}
              <NewAwardsChip count={newAwards} />
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              {!locked && (
                <>
                  <span className="rounded-md bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white dark:bg-blue-500">
                    PE-RUC
                  </span>
                  <span className="tabular-nums">{company.company_ruc}</span>
                </>
              )}
              {company.department_name && (
                <span className="truncate uppercase">{company.department_name}</span>
              )}
            </div>
          </div>
        </button>

        <div className="truncate text-sm text-slate-600 dark:text-slate-400">
          {locked ? (
            <span className="text-xs italic text-slate-400">Al desbloquear</span>
          ) : company.consortia.length > 0 ? (
            <span title={company.consortia.join(", ")}>
              {company.consortia[0]}
              {company.consortia.length > 1 && (
                <span className="ml-1 text-xs font-semibold text-slate-400">
                  +{company.consortia.length - 1}
                </span>
              )}
            </span>
          ) : (
            <span className="text-xs italic text-slate-400">Ninguno</span>
          )}
        </div>

        <div className="text-center text-sm font-bold tabular-nums text-slate-700 dark:text-slate-200">
          {formatNumber(company.num_contracts)}
        </div>

        <div className="text-sm text-slate-600 dark:text-slate-400">
          {locked ? formatMonth(company.last_award_at) : formatDate(company.last_award_at)}
        </div>

        <div className="flex min-w-0 flex-col items-end gap-1">
          <span
            className="text-sm font-extrabold tabular-nums"
            title={locked ? "Monto aproximado: el exacto se ve al desbloquear" : undefined}
          >
            {locked && "≈ "}
            {formatMoney(company.total_amount)}
          </span>
          {matches.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              className="flex cursor-pointer items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold tabular-nums text-emerald-700 transition-colors hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60"
            >
              {matches.length} {matches.length === 1 ? "ítem" : "ítems"} ·{" "}
              {formatMoney(matchesAmount)}
              <ChevronDown
                className={cn("size-3.5 transition-transform", expanded && "rotate-180")}
              />
            </button>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          {locked ? (
            <button
              type="button"
              onClick={onUnlock}
              disabled={unlocking}
              className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-wait disabled:opacity-70"
            >
              {unlocking ? <Loader2 className="size-3.5 animate-spin" /> : <Lock className="size-3.5" />}
              Desbloquear · 1 vista
            </button>
          ) : (
            <button
              type="button"
              onClick={onToggleFollow}
              aria-pressed={following}
              className={cn(
                "flex cursor-pointer items-center gap-1 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors",
                following
                  ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
                  : "bg-card text-foreground hover:bg-accent",
              )}
            >
              {following && <Check className="size-3.5" />}
              {following ? "Siguiendo" : "Seguir"}
            </button>
          )}
          <button
            type="button"
            onClick={onOpen}
            aria-label="Ver empresa"
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border text-slate-400 transition-colors hover:bg-accent hover:text-slate-600"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-5">
          <div className="mb-2 pl-[52px] text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Ítems coincidentes en sus obras
          </div>
          <div className="grid grid-cols-2 gap-2 pl-[52px] xl:grid-cols-4">
            {matches.map((match) => (
              <div
                key={`${match.keyword}-${match.description}`}
                className="flex items-start justify-between gap-3 rounded-xl bg-emerald-50 px-3 py-2 text-[11px] font-bold uppercase text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"
              >
                <span className="min-w-0 break-words">{match.description}</span>
                <span className="shrink-0 tabular-nums">{formatMoney(match.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
